import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity, Onderneming} from "@snelstart-ventures/fiscaal/domain-logic";
import {filter, Observable, shareReplay, take, tap} from "rxjs";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-gebouwen-eigen',
  templateUrl: './spec-gebouwen-eigen.component.html',
  styleUrls: ['./spec-gebouwen-eigen.component.scss'],
  imports: [
    SharedUiDialogModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class SpecGebouwenEigenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public dialogVisible = false;
  public isPartner = false;

  public formGroup = new FormGroup({
    waardeOpening: new FormControl<number | null>(null),
    waardeSlot: new FormControl<number | null>(null),
    aanschafWaarde: new FormControl<number | null>(null),
    restWaarde: new FormControl<number | null>(null, Validators.required),
    bodemwaarde: new FormControl<number | null>(null, Validators.required),
    gebruikBinnenEigenOnderneming: new FormControl<boolean | null>(true),
    omschrijving: new FormControl<string | null>('bedrijfsGebouwen')
  })

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  showDialog() {
    this.handleSwitch(this.dataService.incomeTaxEntity.value, this.isPartner);
    this.dialogVisible = true;
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.resetFormGroup();
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private resetFormGroup() {
    this.formGroup.setValue({
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number,
      aanschafWaarde: null as unknown as number,
      restWaarde: null as unknown as number,
      bodemwaarde: null as unknown as number,
      gebruikBinnenEigenOnderneming: true,
      omschrijving: 'bedrijfsGebouwen'
    }, {emitEvent: false})
  }

  private patchFormGroups(ondernemingen: Onderneming[]) {
    this.resetFormGroup();
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    if (onderneming.materieleVasteActiva?.at(0)) {
      this.formGroup.patchValue(onderneming.materieleVasteActiva[0], {emitEvent: false});
    }
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        // Clone or create the array
        const materieleVasteActiva = Array.isArray(onderneming.materieleVasteActiva) ? [...onderneming.materieleVasteActiva] : [];
        // Ensure the array has at least 1 item
        while (materieleVasteActiva.length < 1) {
          materieleVasteActiva.push({});
        }
        // Update the first item
        materieleVasteActiva[0] = formValue;

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          materieleVasteActiva,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
